import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

type EventHandler = (event: { type: string; [key: string]: unknown }) => void;

/**
 * Subscribe to SSE project events. Reconnects automatically on disconnect.
 * Returns nothing — just call with a handler that reacts to events.
 */
export function useProjectEvents(projectSlug: string | undefined, onEvent: EventHandler) {
  const { accessToken } = useAuth();
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  const connect = useCallback(() => {
    if (!projectSlug || !accessToken) return undefined;

    const url = `/api/projects/${projectSlug}/events?token=${encodeURIComponent(accessToken)}`;
    const es = new EventSource(url);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        handlerRef.current(data);
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      es.close();
      // Reconnect after 5s
      setTimeout(() => connect(), 5000);
    };

    return es;
  }, [projectSlug, accessToken]);

  useEffect(() => {
    const es = connect();
    return () => es?.close();
  }, [connect]);
}
